import React, { useEffect } from 'react';
import { useLanguage } from '../i18n/LanguageContext';
import { useCms, pickLang } from '../context/CmsContext';
import { OtherGroup, TeamMember } from '../lib/api';
import team1 from '../assets/team1.jpg';
import team2 from '../assets/team2.jpg';
import team3 from '../assets/team3.jpg';
import team4 from '../assets/team4.jpg';
import './Team.css';

const fallbackImages = [team1, team2, team3, team4];

interface DisplayMember {
  img: string;
  name: string;
  accent: string;
  role: string;
  desc: string;
}

// 简介要点：按换行拆分，去掉空行
const toPoints = (desc: string): string[] =>
  desc
    .split('\n')
    .map((s) => s.trim())
    .filter(Boolean);

const Team: React.FC = () => {
  const { t, currentLang } = useLanguage();
  const { team, others } = useCms();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // CMS 只存中英两种文案，其余语言取英文；zh 走中文译名
  const isZh = currentLang === 'zh';
  const bi = (o: Record<string, string> | undefined): string =>
    ((isZh ? o?.zh : o?.[currentLang] || o?.en || o?.zh) || '').trim();

  // 后台数据不可用时回退到内置内容
  const members: DisplayMember[] =
    team.length > 0
      ? team.map((m: TeamMember, i) => ({
          img: m.image || fallbackImages[i % fallbackImages.length],
          name: bi(m.name) || pickLang(m.role, currentLang, ''),
          accent: bi(m.accent),
          role: pickLang(m.role, currentLang, ''),
          desc: pickLang(m.desc, currentLang, ''),
        }))
      : ([1, 2, 3, 4] as const).map((n, i) => {
          const m = t.team[`member${n}`];
          return {
            img: fallbackImages[i],
            name: m.name,
            accent: m.accent,
            role: m.role,
            desc: m.desc,
          };
        });

  const otherGroups =
    others.length > 0
      ? others.map((g: OtherGroup) => ({
          label: bi(g.label),
          names: ((isZh
            ? g.names.zh
            : (g.names as Record<string, string[]>)[currentLang] ||
              g.names.en ||
              g.names.zh) || []).filter(Boolean),
        }))
      : t.team.others.map((g) => ({ label: g.label, names: g.names.filter(Boolean) }));

  // 表格按行对齐：第 r 行显示各组的第 r 个名字
  const maxRows = otherGroups.reduce((n, g) => Math.max(n, g.names.length), 0);
  const tableRows = Array.from({ length: maxRows }, (_, r) =>
    otherGroups.map((g) => g.names[r] || '')
  );

  const renderCard = (m: DisplayMember, i: number) => {
    const points = toPoints(m.desc);
    const showAccent = m.accent && m.accent.toLowerCase() !== m.name.toLowerCase();
    return (
      <div key={i} className={`team-card ${points.length ? 'team-card-wide' : 'team-card-compact'}`}>
        <div className="card-side">
          <div className="card-avatar">
            <img
              src={m.img}
              alt={m.name}
              onError={(e) => {
                // 后台图片缺失时回退内置图片
                const fb = fallbackImages[i % fallbackImages.length];
                if (fb && e.currentTarget.src !== fb) {
                  e.currentTarget.src = fb;
                }
              }}
            />
          </div>
          <div className="card-name">{m.name}</div>
          {showAccent && <div className="card-accent">{m.accent}</div>}
          <div className="card-role">{m.role}</div>
        </div>
        {points.length > 0 && (
          <ul className="card-points">
            {points.map((p, j) => (
              <li key={j}>{p}</li>
            ))}
          </ul>
        )}
      </div>
    );
  };

  // 第一行：Dark Sloop（紧凑卡）+ 两位宽卡；第二行：威尔·莱特 + 其他成员表
  const coreMembers = members.slice(0, 3);
  const willWright = members[3];

  return (
    <div className="team-page">
      <section className="team-hero">
        <div className="hero-content">
          <div className="hero-badge">{t.team.badge}</div>
          <h1 className="hero-title">
            <span className="gradient-text">{t.team.hero_title1}</span>{' '}
            <span>{t.team.hero_title2}</span>
          </h1>
          <p className="hero-subtitle">{t.team.hero_subtitle}</p>
        </div>
      </section>

      <div className="team-content">
        <div className="team-intro">
          <p>{t.team.intro}</p>
        </div>

        <div className="team-core">{coreMembers.map(renderCard)}</div>

        <div className="team-bottom">
          {willWright && renderCard(willWright, 3)}

          <div className="team-card team-others">
            <h3 className="others-title">{t.team.others_title}</h3>
            {otherGroups.length > 0 && (
              <div className="others-scroll">
                <table className="others-table">
                  <thead>
                    <tr>
                      {otherGroups.map((g, i) => (
                        <th key={i}>{g.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {tableRows.map((row, r) => (
                      <tr key={r}>
                        {row.map((name, c) => (
                          <td key={c}>{name}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Team;
